import { useMutation, useQueryClient } from '@tanstack/react-query';
import { growthKeys } from '@/data/queryKeys';
import { useGrowthQuery } from '@/data/useGrowthQuery';
import { useNotify } from '@/data/notifications';
import { resolveCampaignBackendId } from '@/data/marketingScope';
import {
  exportCampaignReport,
  fetchCampaignReport,
  pauseCampaignFromReport,
} from '../api-report';

export function useCampaignReport(campaignRouteId: string) {
  const queryClient = useQueryClient();

  return useGrowthQuery({
    queryKey: growthKeys.campaigns.report(campaignRouteId),
    queryFn: () => {
      const backendId = resolveCampaignBackendId(queryClient, campaignRouteId);
      return fetchCampaignReport(backendId);
    },
    enabled: Boolean(campaignRouteId),
  });
}

export function useCampaignReportMutations(campaignRouteId: string) {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [...growthKeys.all, 'campaigns'] });

  const pause = useMutation({
    mutationFn: () => {
      const backendId = resolveCampaignBackendId(queryClient, campaignRouteId);
      return pauseCampaignFromReport(backendId);
    },
    onSuccess: () => {
      notify.success('Campaign paused');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const exportReport = useMutation({
    mutationFn: () => {
      const backendId = resolveCampaignBackendId(queryClient, campaignRouteId);
      return exportCampaignReport(backendId);
    },
    onSuccess: () => notify.success('Report downloaded'),
    onError: (e) => notify.error(e),
  });

  return { pause, exportReport };
}
